/**
 * React Query 的缓存 key
 * ---------------------------------------------------------------------------
 * 全部集中在这里，理由很实际：写操作完成后要精确失效相关缓存
 * （比如收款成功后刷新订单列表和工作台指标），key 散落各处就一定会漏。
 *
 * 约定：key 的第一段是模块名，后面跟影响结果的参数。
 * 参数变了就是另一份缓存，所以 storeId 这类必须放进 key。
 */

export const queryKeys = {
  /* 身份与门店 */
  me: () => ["me"] as const,
  stores: () => ["stores"] as const,
  staff: (storeId: string | null) => ["staff", storeId] as const,
  roles: () => ["roles"] as const,

  /* 会员 */
  members: (params: { keyword?: string; storeId?: string | null }) =>
    ["members", params.storeId ?? null, params.keyword ?? ""] as const,
  member: (memberId: string) => ["member", memberId] as const,
  memberTimeline: (memberId: string) =>
    ["member", memberId, "timeline"] as const,
  memberWallets: (memberId: string) => ["member", memberId, "wallets"] as const,

  /* 项目与商品 */
  /**
   * `limit` 也要进 key：选择器把在售项目一次拉满（100 条）后自己筛，
   * 和列表页默认的 20 条是两份不同的数据。不区分的话两边会互相覆盖缓存——
   * 先开过列表页再开预约弹窗，下拉里就只剩 20 项，而且不会有任何报错。
   */
  services: (params: { status?: string; keyword?: string; limit?: number } = {}) =>
    [
      "catalog",
      "services",
      params.status ?? "all",
      params.keyword ?? "",
      params.limit ?? "default",
    ] as const,
  products: (params: { status?: string; keyword?: string } = {}) =>
    [
      "catalog",
      "products",
      params.status ?? "all",
      params.keyword ?? "",
    ] as const,
  packages: (params: { status?: string; keyword?: string } = {}) =>
    [
      "catalog",
      "packages",
      params.status ?? "all",
      params.keyword ?? "",
    ] as const,
  categories: (type?: string) =>
    ["catalog", "categories", type ?? "all"] as const,

  /* 订单 */
  orders: (params: { storeId: string | null; status?: string }) =>
    ["orders", params.storeId ?? null, params.status ?? "all"] as const,
  order: (orderId: string) => ["order", orderId] as const,

  /* 预约、排班与排队 */
  appointments: (storeId: string | null, from: string, to: string) =>
    ["appointments", storeId ?? null, from, to] as const,
  appointmentsToday: (storeId: string | null) =>
    ["appointments", "today", storeId ?? null] as const,
  shifts: (storeId: string | null, from: string, to: string) =>
    ["shifts", storeId ?? null, from, to] as const,
  queue: (storeId: string | null) => ["queue", storeId ?? null] as const,

  /* 提成与工资 */
  commissionRules: () => ["commission-rules"] as const,
  commissions: (params: {
    storeId?: string | null;
    employeeId?: string;
    from?: string;
    to?: string;
    status?: string;
  }) =>
    [
      "commissions",
      params.storeId ?? null,
      params.employeeId ?? "all",
      params.from ?? "",
      params.to ?? "",
      params.status ?? "all",
    ] as const,
  payrollPeriods: () => ["payroll-periods"] as const,

  /* 活动、优惠券与短信 */
  campaigns: () => ["campaigns"] as const,
  coupons: () => ["coupons"] as const,
  smsTemplates: () => ["sms", "templates"] as const,
  smsAutoRules: () => ["sms", "auto-rules"] as const,
  smsAccount: () => ["sms", "account"] as const,
  printerStatus: () => ["printer-status"] as const,
  smsMessages: () => ["sms", "messages"] as const,

  /* 库存 */
  inventoryBalances: (storeId: string | null) =>
    ["inventory", "balances", storeId ?? null] as const,
  inventorySummary: (storeId: string | null) =>
    ["inventory", "summary", storeId ?? null] as const,
  warehouses: (storeId?: string | null) =>
    ["inventory", "warehouses", storeId ?? null] as const,

  /* 财务 */
  expenses: (storeId: string | null, from?: string, to?: string) =>
    ["expenses", storeId ?? null, from ?? "", to ?? ""] as const,
  expenseCategories: () => ["expense-categories"] as const,
  dailyCloses: (storeId: string | null) =>
    ["daily-closes", storeId ?? null] as const,

  /* 报表 */
  reportOverview: (storeId: string | null, from: string, to: string) =>
    ["reports", "overview", storeId ?? null, from, to] as const,
  reportStores: (storeId: string | null, from: string, to: string) =>
    ["reports", "stores", storeId ?? null, from, to] as const,

  /* 员工手机端。都是"本人"数据，服务端按 token 里的 employeeId 过滤，前端传不了别人的
     ID，所以 key 里不放 employeeId。跨账号的隔离靠退出登录时 queryClient.clear()
     完成（见 lib/auth/auth-context.tsx），不要改成"登录后再覆盖"的写法。 */
  employeeToday: () => ["employee", "today"] as const,
  employeeCustomers: (keyword = "") =>
    ["employee", "customers", keyword] as const,
  employeeCustomer: (memberId: string) =>
    ["employee", "customer", memberId] as const,
  employeeEarnings: (from: string, to: string) =>
    ["employee", "earnings", from, to] as const,
  employeeProfile: () => ["employee", "me"] as const,
  employeeLeave: () => ["employee", "leave"] as const,
  notifications: () => ["notifications"] as const,
  notificationUnread: () => ["notifications", "unread-count"] as const,

  /* 设置 */
  settings: (storeId: string | null) => ["settings", storeId ?? null] as const,
  features: () => ["features"] as const,
  supportAccess: () => ["support-access"] as const,
};
